<script>
   /*************************************************************/
   /* ModelCalculator                                           */
   /* -------------------------                                 */
   /* component for training a PCA model                        */
   /*************************************************************/

   import { pca_fit } from '@mdatools-apps/wasm/pca.js';
   import { app_pca_predict, genhash } from '../../utils.js';

   import { Switch, Container, Button, Spinner, ButtonCancel, Range } from 'svelte-controls-basic';

   let {
      WASM,                      // promise with WASM methods
      dataset,                   // object with training set
      outliers = [],             // vector with dataset row numbers (from 0) marked as outliers
      resobj = $bindable(),      // object with PCA results
      model = $bindable()        // object with PCA model
   } = $props();

   // prefix for the label ids the controls below are named by. it must be unique on the page and
   // every tab of the app stays mounted, so it can not be written out by hand
   const uid = $props.id();

   // error handling parameters
   let status = $state('neutral');
   let errMsg = $state('');

   // model calculation parameters — maximum number of components the dataset allows, and the
   // default choice. ncomp is derived so it follows the dataset size, but stays writable so the
   // Range can override it; the override is dropped when maxNComp changes, as before.
   let maxNComp = $derived(dataset ? Math.min(dataset.values.nrows - 2 - outliers.length, dataset.values.ncols - 1, 50) : null);

   // a model needs room for at least one component and each term above can take that away: two
   // rows go to the estimate itself, every excluded object takes one more, and a single variable
   // leaves nothing to decompose. without this the button was offered on a set 'pca_fit' refuses,
   // and the reason came only after the click
   let canFit = $derived(maxNComp !== null && maxNComp >= 1);
   let ncomp = $derived(canFit ? Math.min(maxNComp, 20) : null);

   // says which of the terms ran out, so the message can name the thing the user can change
   let tooSmallMsg = $derived(
      !dataset || canFit ? '' :
      dataset.values.ncols < 2 ? 'A model needs at least two variables.' :
      outliers.length > 0 ?
         'Too many objects are excluded — ' + (dataset.values.nrows - outliers.length) + ' of ' +
         dataset.values.nrows + ' are left, and a model needs at least three.' :
         'A model needs at least three objects.'
   );
   let center = $state(true);
   let scale = $state(false);


   // promise for model calculation method
   let promise = $state();

   /**
    * Fits PCA model and apply it to training set.
    * @param {Object} dataset - object with training set.
    * @param {Number} ncomp - number of PCs in the model.
    * @param {boolean} center - mean center data columns or not.
    * @param {boolean} scale - standardize data columns or not.
    *
    * Method does not return anything but assigns values to the global parameters "model" and "res".
    */
   async function computeModel(dataset, ncomp, center, scale) {
      promise = WASM()
         .then(Module => {
            // fit model and add extra parameters
            const m = pca_fit(Module, dataset.values, ncomp, center, scale, outliers);
            m['hash'] = genhash();                  // unique hash number to trace model updates

            // everything written per variable below has to be filtered and ordered exactly like
            // the columns 'pca_fit' kept: 'varlabels[i]' names variable 'i' of the model, which is
            // row 'i' of the loadings block of the flat 'm.v', and the length of each of these
            // arrays has to equal 'm.ncols'. it holds today only because nothing is excluded - the
            // dataset here is already preprocessed, so a trim step has taken its columns out of
            // both at once. whoever fills 'exclvars' in has to filter these three arrays by it in
            // the same move, along the variable axis and not the component one, or the results
            // export starts pairing numbers with the labels of variables the model never saw. a
            // length check is not enough to catch that, since a wrong order has the right length
            m['exclvars'] = [];                            // array with indices of excluded variables - reserved for future use
            m['varlabels'] = dataset.varlabels;            // array with labels for variables
            m['varindices'] = dataset.varindices;          // variable indices
            m['varvalues'] = dataset.varvalues;            // variable values if available (wavelength, etc.)
            m['varvaluesName'] = dataset.varvaluesName;    // title for variable axis
            m['varvaluesUnits'] = dataset.varvaluesUnits;  // units if variales are represented by numeric values, e.g. nm
            m['varrev'] = dataset.varrev                   // if variables must be reversed
            m['prep'] = [];                                // array with preprocessing methods — reserved for future use

            // make predictions
            const r = app_pca_predict(Module, m, dataset, outliers);

            // set global variables and status of the component
            model = m;
            resobj = r;
            status = 'success';
         })
         .catch(e =>{
            // something went wrong
            console.error(e);
            model = null;
            status = 'error';
            errMsg = e;
         });
   }

   /**
    * Event handler for computing the model
    * @param e — event object.
    */
   function compute() {
      status = 'computing';
      errMsg = null;
      computeModel(dataset, ncomp, center, scale);
   }

   /**
    * Resets the component status.
    */
   function reset() {
      status = 'neutral';
      promise = null;
      resobj = null;
      model = null;
      errMsg = null;
   }
</script>

{#if (dataset && !model) && status !== 'computing'}
   {#if !canFit}
   <Container name="too-small" status="error">
      <p>{tooSmallMsg}</p>
   </Container>
   {:else}
   <!-- settings for global model calculation -->
   <Container name="ncomp" label="# of PCs" id="{uid}-ncomp">
      <Range bind:value={ncomp} min={1} max={maxNComp} step={1} decNum={0} />
   </Container>
   <Container name="center" label="Center" id="{uid}-center">
      <Switch bind:value={center} options={["no", "yes"]} />
   </Container>
   <Container name="scale" label="Scale" id="{uid}-scale">
      <Switch bind:value={scale} options={["no", "yes"]} />
   </Container>
   <Container name="create-model" label=" ">
      <Button onclick={compute} text="Create model" />
   </Container>
   {/if}
{/if}


{#if promise}

   <!-- calculation is in process -->
   {#await promise}
      <Container name="spinner">
         <Spinner />
         <p>Computing global model...</p>
      </Container>
   {/await}

{/if}

{#if model }
<Container name="info">
   <p><span style="color:#a0a0a0;">PCA model:</span> {model.ncomp} PCs{model.center ? ', centered' : ''}{model.scale ? ', scaled' : ''}</p>
   <ButtonCancel onclick={reset} />
</Container>
{/if}


<!-- error message -->
{#if status === 'error' || errMsg}
<Container name="error" status="error">
   <p>{errMsg}</p>
</Container>
{/if}

